import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { initiatePayment, getBillingDetails, createSubscription, saveBillingDetails, resetBillingState } from "../store/billingSlice";
import { showAlert } from "../store/alertSlice";
import { initializeRazorpayPayment } from "../utils/razorpayUtils";
import { buildPaymentPayload } from "../pages/whatsapp/billingConfig";

export const useBillingHandlers = ({
  billing,
  companyId,
  selectedPlan,
  selectedPlanDetails,
  isContinuingToCheckout,
  setIsContinuingToCheckout,
  setCurrentView,
  handlePaymentSuccess,
  handlePaymentDismissed,
  handlePaymentFailure,
}) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (billing.success && billing.paymentData && billing.paymentFlow !== 'wallet') {
      dispatch(resetBillingState());
      initializeRazorpayPayment({
        paymentData: billing.paymentData,
        billingDetails: billing.billingDetails
          ? { email: billing.billingDetails.email, phone: billing.billingDetails.phone }
          : null,
        onSuccess: handlePaymentSuccess,
        onFailure: handlePaymentFailure,
        onDismiss: handlePaymentDismissed,
      });
    }
  }, [billing.success, billing.paymentData]);

  useEffect(() => {
    if (billing.errorPlans) {
      dispatch(showAlert({ message: "Unable to load plans. Please try again.", type: "error" }));
    }
  }, [billing.errorPlans, dispatch]);

  useEffect(() => {
    // Do not show an alert when billing details fail to load; the UI should
    // handle missing billing details silently.
  }, [billing.errorBillingDetails]);

  useEffect(() => {
    if (billing.errorSubscription) {
      const errorMessage = typeof billing.errorSubscription === 'string' 
        ? billing.errorSubscription 
        : billing.errorSubscription.message || "Unable to create subscription. Please try again.";
      
      dispatch(showAlert({ message: errorMessage, type: "error" }));
    }
  }, [billing.errorSubscription, dispatch]);

  useEffect(() => {
    if (billing.error && billing.paymentFlow !== 'wallet') {
      if (billing.error.status === 400) {
        dispatch(showAlert({ message: "Please complete your billing information first.", type: "error" }));
      } else if (billing.error.status === 409) {
      } else {
        dispatch(showAlert({ message: "Payment service error. Please try again.", type: "error" }));
      }
    }
  }, [billing.error, billing.paymentFlow, dispatch]);

  const handleContinueToCheckout = () => {
    if (!selectedPlan) {
      dispatch(showAlert({ message: "Please select a plan first.", type: "error" }));
      return;
    }
    if (isContinuingToCheckout) return;
    setIsContinuingToCheckout(true);
    dispatch(createSubscription({ planId: selectedPlan }));
    dispatch(getBillingDetails());
    setCurrentView("checkout");
  };

  const handleInitiatePayment = () => {
    const subscriptionId = billing.subscription?.subscriptionId;
    if (!subscriptionId) {
      dispatch(showAlert({ message: "Subscription not found. Please try again.", type: "error" }));
      return;
    }
    dispatch(initiatePayment({
      ...buildPaymentPayload({ companyId, subscriptionId, selectedPlanDetails }),
      paymentFlow: 'subscription',
    }));
  };

  const handleSaveBillingDetails = (updatedDetails) => {
    dispatch(saveBillingDetails({ billingDetails: updatedDetails }));
  };

  return {
    handleContinueToCheckout,
    handleInitiatePayment,
    handleSaveBillingDetails,
  };
};
